import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { SignatureCanvasProps } from 'react-signature-canvas'
import { compose } from 'recompose'
import { Field, InjectedFormProps, change, formValueSelector, getFormValues, reduxForm } from 'redux-form'

import {
  DEFAULT_DATE_FORMAT,
  DISPLAY_SHORT_DATE_FORMAT,
  DISPLAY_SHORT_DOT_DELAMINATE_DATE_FORMAT,
} from 'constants/date'
import { noop } from 'constants/global'
import { Option } from 'constants/models'
import { MedicationForm } from 'services/medicationForms/models'
import { REVIEW_MEDICATION_OPTIONS, REVIEW_MEDICATION_STATUSES } from 'services/reviews/constants'
import { STATUS_OPTIONS_VALUES } from 'services/users/constants'

import { isSameDay } from 'utils/date'
import { scrollToElement } from 'utils/dom'
import { isRequired } from 'utils/fieldValidation'
import { createTypeFromEnumValues } from 'utils/typescript'

import { Form, Section, Switch, Typography } from 'components'
import { SelectedImage } from 'components/PhotoUpload/PhotoUpload'

import i18n from 'translations'

import { StyledContainer, StyledSignaturePad } from './ReviewFormStyled'

export const ADD_REVIEW_FORM = 'AddReviewForm'
export const STATUS = 'review'

type AddReviewFormProps = {
  formValues?: AddReviewFormVales
  isLongTerm?: boolean
  isSubmitting?: boolean
  medicationForm?: MedicationForm
  onCancel: () => void
  onSubmit: (fields: AddReviewFormVales) => void
  queryStatus?: string
} & PropsFromRedux

export interface AddReviewFormVales {
  endDate?: moment.Moment | Date
  medicationReturnedDate?: moment.Moment | Date
  nextReviewDate?: Date
  notes?: string
  parent?: Option
  parentSignature?: SelectedImage
  staffMember?: Option
  staffMemberSignature?: SelectedImage
  type?: createTypeFromEnumValues<REVIEW_MEDICATION_STATUSES>
}

const mapState = (state) => ({
  formValues: getFormValues(ADD_REVIEW_FORM)(state),
  type: formValueSelector(ADD_REVIEW_FORM)(state, 'type'),
})

const mapDispatch = {
  changeValue: (field, value) => change(ADD_REVIEW_FORM, field, value),
}

const connector = connect(mapState, mapDispatch)

type PropsFromRedux = ConnectedProps<typeof connector>

const ReviewForm: React.FC<InjectedFormProps<AddReviewFormVales, AddReviewFormProps> & AddReviewFormProps> = ({
  changeValue,
  formValues,
  handleSubmit,
  isLongTerm,
  isSubmitting,
  medicationForm,
  onCancel,
  onSubmit,
  queryStatus,
  type,
}) => {
  const [isInitialized, setIsInitialized] = useState<boolean>(false)

  const parentSignatureRef = useRef<SignatureCanvasProps>(null)
  const staffMemberSignatureRef = useRef<SignatureCanvasProps>(null)

  const { parent, staffMember } = formValues
  const { child, endDate: medicationEndDate, medicationDetails } = medicationForm
  const { expiryDate, storeOnSite } = medicationDetails

  useEffect(() => {
    if (queryStatus) {
      scrollToElement(document.getElementById(queryStatus))
    }
  }, [queryStatus])

  useEffect(() => {
    setIsInitialized(true)
  }, [])

  useEffect(() => {
    if (isInitialized && parentSignatureRef.current) {
      parentSignatureRef.current.clearSignature()
    }

    if (!parent) {
      changeValue('parentSignature', null)
    }
  }, [parent])

  useEffect(() => {
    if (isInitialized && staffMemberSignatureRef.current) {
      staffMemberSignatureRef.current.clearSignature()
    }

    if (!staffMember) {
      changeValue('staffMemberSignature', null)
    }
  }, [staffMember])

  const renderSignature = () => (
    <Section.SubSection title={i18n.t('modals:Administration:AddAdministration:Form:Signatures:title')}>
      <Form.Row
        label={i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:StaffMember:label')}
        width={{ field: '300px' }}
        required
        verticalLabel
      >
        <Field
          component={Form.InfiniteDropdowns.Memberships}
          name="staffMember"
          placeholder={i18n.t('modals:Administration:AddAdministration:Form:Signatures:StaffDropdown:placeholder')}
          status={STATUS_OPTIONS_VALUES.ACTIVE}
          validate={isRequired}
          searchable
        />
      </Form.Row>
      {staffMember && (
        <Form.Row
          label={i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:StaffSignature:label')}
          width={{ field: '80%' }}
          required
          verticalLabel
        >
          <StyledSignaturePad>
            <Field
              component={Form.SignaturePad}
              name="staffMemberSignature"
              signatureRef={staffMemberSignatureRef}
              validate={isRequired}
              searchable
            />
          </StyledSignaturePad>
        </Form.Row>
      )}
      <Form.Row
        label={i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:ParentGuardian:label')}
        width={{ field: '300px' }}
        required
        verticalLabel
      >
        <Field
          childId={child?.id}
          component={Form.InfiniteDropdowns.Carers}
          name="parent"
          placeholder={i18n.t('modals:Administration:AddAdministration:Form:Signatures:WitnessDropdown:placeholder')}
          status={STATUS_OPTIONS_VALUES.ACTIVE}
          validate={isRequired}
          parentalResponsibilityOnly
          searchable
        />
      </Form.Row>
      {parent && (
        <Form.Row
          label={i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:ParentGuardiansSignature:label')}
          width={{ field: '80%' }}
          required
          verticalLabel
        >
          <StyledSignaturePad>
            <Field
              component={Form.SignaturePad}
              name="parentSignature"
              signatureRef={parentSignatureRef}
              validate={isRequired}
              searchable
            />
          </StyledSignaturePad>
        </Form.Row>
      )}
    </Section.SubSection>
  )

  return (
    <StyledContainer id={STATUS}>
      <Typography margin="0 0 20px 0" variant="h5">
        {isLongTerm
          ? i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:title')
          : i18n.t('services:Review:ReviewMedication:endMedication')}
      </Typography>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {isLongTerm && (
          <Form.Row verticalLabel>
            <Field
              component={Form.RadioGroup}
              name="type"
              options={REVIEW_MEDICATION_OPTIONS}
              validate={isRequired}
              buttonStyle
              horizontal
            />
          </Form.Row>
        )}
        {type === REVIEW_MEDICATION_STATUSES.MEDICATION_IS_STILL_NEEDED && (
          <React.Fragment>
            <Form.Row
              label={i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:MedicationFormEndDate:label')}
              width={{ field: '60%' }}
              verticalLabel
            >
              <Typography>
                {medicationEndDate ? moment(medicationEndDate).format(DISPLAY_SHORT_DOT_DELAMINATE_DATE_FORMAT) : null}
              </Typography>
            </Form.Row>
            <Form.Row
              label={i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:MedicationExpiryDate:label')}
              width={{ field: '60%' }}
              verticalLabel
            >
              <Typography>
                {expiryDate
                  ? moment(expiryDate).format(DISPLAY_SHORT_DOT_DELAMINATE_DATE_FORMAT)
                  : null}
              </Typography>
            </Form.Row>
            <Form.Row multipleFieldsInARow>
              <Form.Row.Item>
                <Switch value={storeOnSite} disabled onChange={noop} />
              </Form.Row.Item>
              <Form.Row.TextItem
                margin="0 -17px 0"
                text={i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:MedicationStoredOnSite:label')}
              />
            </Form.Row>
            <Form.Row
              label={i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:ReviewNotes:label')}
              width={{ field: '60%' }}
              verticalLabel
            >
              <Field component={Form.TextAreaField} name="notes" />
            </Form.Row>
            <Form.Row
              label={i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:NextScheduledReviewDate:label')}
              width={{ field: '300px' }}
              required
              verticalLabel
            >
              <Field component={Form.DatePicker} name="nextReviewDate" validate={isRequired} />
            </Form.Row>
          </React.Fragment>
        )}
        {type === REVIEW_MEDICATION_STATUSES.END_MEDICATION && (
          <React.Fragment>
            {storeOnSite && (
              <React.Fragment>
                <Typography>
                  {i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:MedicationMarkedAsStoredOnSite:label')}
                </Typography>
                <Form.Row
                  label={i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:DateMedicationReturned:label')}
                  width={{ field: '300px' }}
                  verticalLabel
                >
                  <Field component={Form.DatePicker} name="medicationReturnedDate" />
                </Form.Row>
              </React.Fragment>
            )}
            <Form.Row
              label={i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:ReviewNotes:label')}
              width={{ field: '60%' }}
              verticalLabel
            >
              <Field component={Form.TextAreaField} name="notes" />
            </Form.Row>
            {renderSignature()}
          </React.Fragment>
        )}
        <Form.FooterActions isSubmitting={isSubmitting} submitLabel={i18n.t('global:Save')} onCancelClick={onCancel} />
      </Form>
    </StyledContainer>
  )
}

const validate = ({ endDate, nextReviewDate }: AddReviewFormVales) => {
  const errors = {
    nextReviewDate: '',
  }

  const reviewDate = new Date()

  if (
    reviewDate
    && nextReviewDate
    && (
      moment(reviewDate).format(DEFAULT_DATE_FORMAT) > moment(nextReviewDate).format(DEFAULT_DATE_FORMAT)
      || isSameDay(reviewDate, nextReviewDate)
    )
  ) {
    errors.nextReviewDate = i18n.t(
      'module:Safeguarding:MedicationForm:Preview:ReviewForm:validationMessage:NextReviewDate:lessThenReviewDate',
      {
        reviewDate: moment(reviewDate).format(DISPLAY_SHORT_DATE_FORMAT),
      },
    )
  }

  if (
    nextReviewDate
    && endDate
    && moment(nextReviewDate).format(DEFAULT_DATE_FORMAT) > moment(endDate).format(DEFAULT_DATE_FORMAT)
  ) {
    errors.nextReviewDate = i18n.t(
      'module:Safeguarding:MedicationForm:Preview:ReviewForm:validationMessage:NextReviewDate:lessThenExpiry',
    )
  }

  return errors
}

const enhance = compose(
  reduxForm<AddReviewFormVales, AddReviewFormProps>({ enableReinitialize: true, form: ADD_REVIEW_FORM, validate }),
  connector,
)

export default enhance(ReviewForm)
